/*
 * NOTE: This reducer's scope has changed!
 * It no longer houses actual validations, those have moved to selectors.
 * It now only houses validations-support data.
 */
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import { getTriggerPropertiesMap } from 'reducers/flowDesigner/triggerOutputs';
import * as constantsModalActions from 'actions/constantsModal';
import * as designerActions from 'actions/flowDesigner';
import {
  FETCH_SCRIPT_STEP_SUCCESS,
  FETCH_SCRIPT_STEPS_SUCCESS,
} from 'actions/flowDesigner/scriptSteps';
import * as endpointsModalActions from 'actions/endpointsModal';
import * as flowBlockInputActions from 'actions/flowBlockInput';
import * as scriptStepModalAction from 'actions/scriptStepModal';
import * as validateActions from 'actions/flowValidations';
import { CREATE_FORM_SECTION_SUCCESS } from 'actions/flowDesigner/canvas';
import { mockBuiltinInputs } from 'xm-constants/flowInput';

import {
  buildConstantMap,
  buildEndpointMap,
  buildOutputMap,
  buildScriptStepMap,
  deleteConstant,
  deleteScriptStep,
  unpackCanvasPagedFields,
  updateEndpoint,
  updateScriptStep,
} from './utils';

import { addToPropertyMaps } from './canvasListValidations';

const {
  IB_ENDPOINT_CREATED,
  FBI_FETCH_PLAN_CONSTANT_SUCCESS,
  FBI_FETCH_LAYOUT_FOR_CREATE_EVENT_INPUTS_UPDATE_SUCCESS,
} = flowBlockInputActions;

const {
  FETCH_CANVAS_FOR_VALIDATION_SUCCESS,
  VALIDATE_FETCH_PLAN_END_POINTS_SUCCESS,
  VALIDATE_SCRIPT_STEP_REMOVED,
  VALIDATE_FETCH_LAYOUT_FOR_LIST_VALIDATION_SUCCESS,
  FLOW_VALIDATION_SET_PLAN_ID,
  ADD_VALIDATION_CANVAS_DATA,
  VALIDATE_DELETED_CANVAS,
} = validateActions;

const {
  FETCH_INTEGRATION_STEPS_SUCCESS,
  FETCH_ALL_FORM_RESPONSES_SUCCESS,
  FETCH_BUILT_IN_INPUTS,
  FETCH_BUILT_IN_INPUTS_SUCCESS,
  FLOW_DESIGNER_LEAVE_WORKFLOW,
} = designerActions;

const { SCRIPT_STEP_MODAL_SAVE_SUCCESS } = scriptStepModalAction;

// https://xmatters.atlassian.net/wiki/spaces/ENG/pages/2275765/Warning+and+Errors+States+for+Steps
export const defaultValidationState = {
  constantMap: {},
  endpointMap: {},
  outputMap: {},
  stepMap: {},
  canvasData: {},
  // for canvas list validations
  propertyMaps: {},
  layoutByFormIdMap: {},
  // responses for all forms
  allResponses: {},
  startedValidation: false,
  triggerOutputProperties: getTriggerPropertiesMap(),
};

export default function validationHandleFlowChanges(
  state = defaultValidationState,
  action,
) {
  const { type, payload } = action;

  switch (type) {
    case VALIDATE_DELETED_CANVAS: {
      const id = payload;
      const filteredCanvasData = omit(state.canvasData, id);
      return {
        ...state,
        canvasData: filteredCanvasData,
      };
    }
    case FLOW_DESIGNER_LEAVE_WORKFLOW: {
      return {
        ...defaultValidationState,
      };
    }
    case FLOW_VALIDATION_SET_PLAN_ID: {
      const planId = payload;
      return {
        ...state,
        /**
         * Remove canvas data associated with other plans, because somehow Optimus is getting an
         * edge case where FLOW_DESIGNER_LEAVE_WORKFLOW doesn't trigger.
         */
        startedValidation: true,
        canvasData: pick(
          state.canvasData,
          canvas => canvas?.plan?.id === planId,
        ),
      };
    }
    case FETCH_SCRIPT_STEPS_SUCCESS: {
      return {
        ...state,
        stepMap: buildScriptStepMap(state.stepMap, payload.data),
        outputMap: buildOutputMap(state.outputMap, payload.data),
      };
    }
    case FETCH_SCRIPT_STEP_SUCCESS: {
      return {
        ...state,
        stepMap: buildScriptStepMap(state.stepMap, [payload]),
        outputMap: buildOutputMap(state.outputMap, [payload]),
      };
    }
    case FETCH_INTEGRATION_STEPS_SUCCESS: {
      return {
        ...state,
        stepMap: buildScriptStepMap(state.stepMap, payload.data),
        outputMap: buildOutputMap(state.outputMap, payload.data),
      };
    }
    case VALIDATE_FETCH_PLAN_END_POINTS_SUCCESS:
      return {
        ...state,
        endpointMap: buildEndpointMap(payload.data),
      };
    case FBI_FETCH_PLAN_CONSTANT_SUCCESS:
      return {
        ...state,
        constantMap: buildConstantMap(payload.data),
      };
    case FBI_FETCH_LAYOUT_FOR_CREATE_EVENT_INPUTS_UPDATE_SUCCESS: {
      const { formId, data } = action.payload;
      return {
        ...state,
        layoutByFormIdMap: {
          ...state.layoutByFormIdMap,
          [formId]: data,
        },
      };
    }
    case SCRIPT_STEP_MODAL_SAVE_SUCCESS: {
      return {
        ...state,
        stepMap: updateScriptStep(state.stepMap, payload),
      };
    }
    case VALIDATE_SCRIPT_STEP_REMOVED: {
      return {
        ...state,
        stepMap: deleteScriptStep(state.stepMap, payload),
      };
    }
    case constantsModalActions.CONSTANT_DELETE_SUCCESS: {
      return {
        ...state,
        constantMap: deleteConstant(state.constantMap, payload.name),
      };
    }
    case constantsModalActions.CONSTANT_SAVE_SUCCESS:
      return {
        ...state,
        constantMap: { ...state.constantMap, [payload.name]: payload },
      };
    case VALIDATE_FETCH_LAYOUT_FOR_LIST_VALIDATION_SUCCESS: {
      const { formId, data } = action.payload;
      const stateWithNewLayout = {
        ...state,
        layoutByFormIdMap: {
          ...state.layoutByFormIdMap,
          [formId]: data,
        },
      };

      return {
        ...stateWithNewLayout,
      };
    }
    case CREATE_FORM_SECTION_SUCCESS: {
      const { id: formId } = action.payload.form;
      const outputsOfThisForm = [
        ...(state.layoutByFormIdMap[formId] || []),
        action.payload,
      ];

      return {
        ...state,
        layoutByFormIdMap: {
          ...state.layoutByFormIdMap,
          [formId]: outputsOfThisForm,
        },
      };
    }
    case IB_ENDPOINT_CREATED:
    case endpointsModalActions.ENDPOINT_SAVE_SUCCESS: {
      return {
        ...state,
        endpointMap: updateEndpoint(state.endpointMap, action.payload),
      };
    }
    case validateActions.VALIDATE_RECEIVED_FORM_PROPERTIES: {
      const { canvasId, properties } = payload;
      const { propertyMaps } = state;
      return {
        ...state,
        propertyMaps: addToPropertyMaps(propertyMaps, canvasId, properties),
      };
    }
    case ADD_VALIDATION_CANVAS_DATA:
    case FETCH_CANVAS_FOR_VALIDATION_SUCCESS: {
      return {
        ...state,
        canvasData: {
          ...state.canvasData,
          [payload.id]: unpackCanvasPagedFields(payload, state),
        },
      };
    }
    case FETCH_ALL_FORM_RESPONSES_SUCCESS: {
      return {
        ...state,
        allResponses: {
          ...state.allResponses,
          ...payload.data.reduce((m, r) => ({ ...m, [r.id]: r }), {}),
        },
      };
    }
    case FETCH_BUILT_IN_INPUTS_SUCCESS:
    case FETCH_BUILT_IN_INPUTS: {
      // TODO: update when the `/built-in-inputs` endpoint is implemented:
      const builtInInputs = mockBuiltinInputs;

      return {
        ...state,
        builtInInputs,
      };
    }
    default:
      return state;
  }
}
